import { FontAwesome } from '@expo/vector-icons';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  ActivityIndicator,
  BackHandler, Modal, Platform, Pressable, ScrollView, StatusBar, StyleSheet, Text, TextInput,
  View,
} from 'react-native';
import * as Animatable from 'react-native-animatable';
import { useSelector } from 'react-redux';
import BottomSheet, {
  BottomSheetBackdrop, BottomSheetModal, BottomSheetScrollView, BottomSheetView,
} from '@gorhom/bottom-sheet';
import { LinearGradient } from 'expo-linear-gradient';
import {
  initialWindowMetrics, SafeAreaView, useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import UserIcon16 from '../components/16pxIcons/UserIcon16';
import EditIcon from '../components/EditIcon';
import SearchIcon from '../components/SearchIcon';
import UserGroupCard from '../components/UserManagement/UserGroupCard';
import Colors from '../constants/Colors';
import { RootTabScreenProps } from '../types';
import GroupEditModal from '../components/UserManagement/GroupEditModal';
import UserRowWeb from '../components/UserManagement/UserRowWeb';
import getUserGroups from '../requests/Users/GetUserGroups';
import getUsers from '../requests/Users/GetUsers';
import AlertModal from '../components/AlertModal';
import InviteUserModal from '../components/UserManagement/InviteUserModal';
import ScreenEmptyState from '../components/ScreenEmptyState';
import NoUsersImage from '../components/UserManagement/NoUsersImage';
import isMobile from '../components/IsMobile';
import { logEvent } from '../components/LogEvent';
import {
  UserGroup, UserInstance, UserPermission, UserStatus,
} from '../constants/Types';
import {
  CREATE_ROLE_ERROR, CREATE_ROLE_SUCCESS,
  DELETE_USER_GROUP_SUCCESS, DELETE_USER_SUCCESS, EDIT_ROLE_ERROR,
  EDIT_ROLE_SUCCESS, noPermissionsSubtitle,
  noPermissionsTitle, SAVE_USER_GROUP_SUCCESS, SEND_INVITE_SUCCESS, SOMETHING_WENT_WRONG,
} from '../constants/AlertMessages';
import SearchIcon24 from '../components/24pxIcons/SearchIcon24';
import { getUserFullName } from '../constants/Utils';
import getRoles from '../requests/Users/GetRoles';
import RightArrow from '../components/16pxIcons/RightArrow';
import EditRoleModal from '../components/UserManagement/EditRoleModal';
import createRole from '../requests/Users/CreateRole';
import editRole from '../requests/Users/EditRole';
import UserGroupIcon16 from '../components/16pxIcons/UserGroupIcon16';
import ListIcon16 from '../components/16pxIcons/ListIcon16';
import PlusIcon from '../components/16pxIcons/PlusIcon';
import RoleMatrix from '../components/UserManagement/RoleMatrix';
import EditIcon24 from '../components/24pxIcons/EditIcon24';
import UserCardMobile from '../components/UserManagement/UserCardMobile';
import Style from '../constants/Style';
import deleteRole from '../requests/Users/DeleteRole';
import UserGroupCardMobile from '../components/UserManagement/UserGroupCardMobile';
import getNotificationGroups from '../requests/Users/GetNotificationGroups';
import NotificationGroupCard from '../components/UserManagement/NotificationGroupCard';
import deleteUser from '../requests/Users/DeleteUser';
import BackArrow from '../components/BackIcon';
import SearchBar from '../components/SearchBar';
import FilterIconMobile16 from '../components/16pxIcons/FilterIconMobile';
import InviteUserBottomSheet from '../components/UserManagement/InviteUserBottomSheet';
import PlusIcon16 from '../components/16pxIcons/PlusIcon16';
import Button from '../components/Button';
import DeleteIcon24 from '../components/24pxIcons/DeleteIcon24';
import TriangleDown from '../components/TriangleDown';
import CloseModalIcon from '../components/CloseModalIcon';
import PressableStyled from '../components/PressableStyled';
import UserGroupBottomSheet from '../components/UserManagement/UserGroupBottomSheet';
import CustomTextField from '../components/CustomTextField';
import updateUserGroupUsers from '../requests/Users/UpdateUserGroupUsers';

// eslint-disable-next-line no-unused-vars
export default function UserMgmtScreen({ navigation }: RootTabScreenProps<'UserManagement'>) {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canViewUserGroups: boolean = isAdmin
    || permissions.includes(UserPermission.USER_GROUPS_READ);
  const canViewUsers: boolean = isAdmin || permissions.includes(UserPermission.USERS_READ);
  const canAddUserGroups: boolean = isAdmin
    || permissions.includes(UserPermission.USER_GROUPS_CREATE);
  const canAddUsers: boolean = isAdmin || permissions.includes(UserPermission.USERS_CREATE);
  const canUpdateUsers: boolean = isAdmin || permissions.includes(UserPermission.USERS_UPDATE);
  const canDeleteUsers: boolean = isAdmin || permissions.includes(UserPermission.USERS_DELETE);
  const canAddRoles: boolean = isAdmin || permissions.includes(UserPermission.ROLES_CREATE);
  const canUpdateRoles: boolean = isAdmin || permissions.includes(UserPermission.ROLES_UPDATE);
  const isDev = useSelector((state: { devState: { isDev: boolean } }) => state.devState.isDev);
  const buildingId: string = useSelector((state: { selectedBuilding: { id: string } }) => state
    .selectedBuilding.id);
  const [searchDropdownVisible, setSearchDropdownVisible] = useState<boolean>(false);
  const [searchDropdownSelection, setSearchDropdownSelection] = useState<string>(isMobile ? 'Groups' : 'Users');
  const [searchValue, setSearchValue] = useState<string>('');
  const [sortDropdownVisible, setSortDropdownVisible] = useState<boolean>(false);
  const [sortDropdownSelection, setSortDropdownSelection] = useState('by alphabet A-Z');
  const [selectedSection, setSelectedSection] = useState<'groups' | 'users' | 'roles'>('groups');
  const [showSectionDropdown, setShowSectionDropdown] = useState<boolean>(false);
  const [addGroupsModalVisible, setAddGroupsModalVisible] = useState<boolean>(false);
  const [inviteModalVisible, setInviteModalVisible] = useState<boolean>(false);
  const [allUsers, setAllUsers] = useState([]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [updateUsers, setUpdateUsers] = useState<boolean>(false);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertHeading, setAlertHeading] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string | React.ReactElement>('');
  const [searchFocused, setSearchFocused] = useState<boolean>(false);
  const [availableUsersSelected, setAvailableUsersSelected] = useState<boolean>(true);
  const [groupUsersTabSelected, setGroupUsersTabSelected] = useState<boolean>(true);
  const [roles, setRoles] = useState<object[]>([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedUserGroup, setSelectedUserGroup] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [showRoleMenu, setShowRoleMenu] = useState(false);
  const [editUserGroup, setEditUserGroup] = useState(false);
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string>(null);
  const [userGroupUsers, setUserGroupUsers] = useState<UserInstance[]>([]);
  const [notificationGroups, setNotificationGroups] = useState(undefined);
  const [, setSortEnabled] = useState<boolean>(false);
  const [filterEnabled, setFilterEnabled] = useState<boolean>(false);
  const [filterDropdownVisible, setFilterDropdownVisible] = useState<boolean>(false);
  const [filterDropdownIndex, setFilterDropdownIndex] = useState<number | null>(null);
  const [filterItemDropdownIndex, setFilterItemDropdownIndex] = useState<number | null>(null);
  const [draftFilters, setDraftFilters] = useState<any>([]);
  const [appliedFilters, setAppliedFilters] = useState<any>([]);
  const [draftSortEnabled, setDraftSortEnabled] = useState<boolean>(false);
  const [draftSortDropdownSelection, setDraftSortDropdownSelection] = useState('by alphabet A-Z');
  const [appliedSortEnabled, setAppliedSortEnabled] = useState<boolean>(false);
  const [appliedSortDropdownSelection, setAppliedSortDropdownSelection] = useState('by alphabet A-Z');
  const [openedGroupCards, setOpenedGroupCards] = useState<Record<string, boolean>>({});

  const topInset = useSafeAreaInsets().top;
  const modalTopInset = Platform.OS === 'android' ? Math.max(
    topInset,
    initialWindowMetrics?.insets.top ?? 0,
    StatusBar.currentHeight ?? 0,
    32,
  ) : 0;

  const roleBottomSheetRef = useRef<BottomSheetModal>(null);
  const userFilterBottomSheetRef = useRef<BottomSheetModal>(null);
  const userInviteBottomSheetRef = useRef<BottomSheetModal>(null);
  const userGroupBottomSheetRef = useRef<BottomSheetModal>(null);

  const cloneFilters = (sourceFilters: any[] = []) => sourceFilters
    .map((filter) => ({ ...filter }));

  const resetFilterDraftState = useCallback(() => {
    setDraftFilters(cloneFilters(appliedFilters));
    setFilterEnabled(appliedFilters.length > 0);
    setDraftSortEnabled(appliedSortEnabled);
    setDraftSortDropdownSelection(appliedSortDropdownSelection);
    setFilterDropdownIndex(null);
    setFilterItemDropdownIndex(null);
    setSortDropdownVisible(false);
  }, [appliedFilters, appliedSortEnabled, appliedSortDropdownSelection]);

  const openUserFilterMenu = useCallback(() => {
    resetFilterDraftState();
    userFilterBottomSheetRef.current?.present();
  }, [resetFilterDraftState]);

  const closeUserFilterMenu = useCallback(() => {
    userFilterBottomSheetRef.current?.dismiss();
  }, []);

  const showAlert = (
    title: string,
    message: string | React.ReactElement,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = '',
    heading: string = '',
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertVisible(true);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setAlertHeading(heading);
  };

  const removeUserFromSelectedGroup = (user: UserInstance) => updateUserGroupUsers(
    selectedUserGroup?.UserGroupId,
    [],
    [user.id],
    selectedUserGroup?.Name ?? '',
    buildingId,
  ).then((res) => {
    if (res?.isError) {
      showAlert('Error', res.message || SOMETHING_WENT_WRONG, true);
    } else {
      setUpdateUsers(!updateUsers);
      showAlert(
        `${getUserFullName(user)} removed`,
        <>
          <Text style={Style.alertBoldText}>{getUserFullName(user)}</Text>
          {' has been removed from '}
          <Text style={Style.alertBoldText}>{selectedUserGroup?.Name}</Text>
        </>,
        false,
      );
    }
  });

  const handleClick: () => void = () => {
    setSearchDropdownVisible(false);
    setSortDropdownVisible(false);
  };

  const loadUserGroups: () => void = () => {
    getUserGroups().then((response) => {
      setLoading(false);
      if (response?.UserGroups?.length > 0) {
        setUserGroups(response.UserGroups);
      }
    });
  };

  useEffect(() => {
    if (Platform.OS === 'web') {
      // eslint-disable-next-line no-undef
      window.addEventListener('click', handleClick, true);
      // eslint-disable-next-line no-undef
      return window.removeEventListener('click', handleClick);
    }
    return () => { };
  }, []);

  useEffect(() => {
    if (showRoleMenu) roleBottomSheetRef?.current?.expand();
    else roleBottomSheetRef?.current?.close();
  }, [showRoleMenu]);

  useEffect(() => {
    if (!selectedRole) {
      setShowRoleMenu(false);
      roleBottomSheetRef?.current?.close();
    }
  }, [selectedRole]);

  useEffect(() => {
    if (selectedUserGroup) {
      getUsers({ userGroupId: selectedUserGroup.UserGroupId }).then((response) => {
        setUserGroupUsers(response?.items ?? []);
      });
      getNotificationGroups(selectedUserGroup.UserGroupId).then((response) => {
        if (response?.Prototypes?.length > 0) {
          setNotificationGroups(response.Prototypes);
        } else setNotificationGroups([]);
      });
    } else setUserGroupUsers([]);
  }, [selectedUserGroup, updateUsers]);

  useEffect(() => {
    loadUserGroups();
    const roleFilter = appliedFilters.find((filter) => filter.type === 'Role');
    const nameFilter = appliedFilters.find((filter) => filter.type === 'Name');
    const emailFilter = appliedFilters.find((filter) => filter.type === 'Email');
    getUsers({
      roleId: roleFilter?.id,
      name: nameFilter?.value,
      email: emailFilter?.value,
    }).then((response) => {
      if (response?.items?.length > 0) {
        setAllUsers(response.items);
      } else setAllUsers([]);
    });
    getRoles().then((response) => {
      // console.log('Roles response:', response);
      if (response?.length > 0) {
        setRoles(response);
        if (selectedRole) {
          setSelectedRole(response.find((role) => role.id === selectedRole.id) || null);
        }
      }
    });
  }, [updateUsers, appliedFilters]);

  useFocusEffect(
    useCallback(() => {
      const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
        if (selectedRole) {
          setShowRoleMenu(false);
          setSelectedRole(null);
          return true;
        }
        if (selectedUserGroup) {
          setSelectedUserGroup(null);
          return true;
        }

        return false;
      });

      return () => backHandler.remove();
    }, [selectedRole, selectedUserGroup]),
  );

  useEffect(loadUserGroups, [loading]);

  const saveRole = async (name, roleDescription, rolePermissions, id, roleColor) => {
    if (id) {
      return editRole(name || editingRole?.name || '', roleDescription || editingRole?.description || '', rolePermissions, id, roleColor)
        .then(() => {
          setEditingRole(null);
          setUpdateUsers(!updateUsers);
          if (!isMobile) showAlert(EDIT_ROLE_SUCCESS, '', false);
        }).catch((error) => {
          if (!isMobile) showAlert(EDIT_ROLE_ERROR, error.message, true);
        });
    }
    return createRole(name || '', roleDescription || '', rolePermissions, roleColor).then((response) => {
      if (response?.isError) {
        setSelectedRole(null);
        showAlert(CREATE_ROLE_ERROR, response.errorText || SOMETHING_WENT_WRONG, true, null, '', 'Create Role');
        return;
      }
      setSelectedRole(null);
      setEditingRole(null);
      setUpdateUsers(!updateUsers);
      showAlert(CREATE_ROLE_SUCCESS, '', false, null, '', 'Create Role');
    }).catch((error) => {
      setSelectedRole(null);
      if (!isMobile) showAlert(CREATE_ROLE_ERROR, error.message, true);
    });
  };

  const renderFilterDropdown = (filter, index) => {
    switch (filter.type) {
      case 'Role':
        return (
          <View style={[styles.dropdownBox, { alignSelf: 'stretch' }]}>
            <ScrollView
              style={{ maxHeight: 200 }}
              nestedScrollEnabled
              keyboardShouldPersistTaps='handled'
            >
              {roles.map((role: any) => (
                <Pressable
                  key={role.name}
                  onPress={() => {
                    const newFilters = cloneFilters(draftFilters);
                    newFilters[index].value = role.name;
                    newFilters[index].type = 'Role';
                    newFilters[index].id = role.id;
                    setDraftFilters(newFilters);
                    setFilterDropdownIndex(null);
                  }}
                >
                  <Text style={styles.dropdownBoxText}>{role.name}</Text>
                </Pressable>
              ))}
            </ScrollView>
          </View>);
      case 'Email':
        return (
          <View style={[styles.dropdownBox, { alignSelf: 'stretch' }]}>
            <ScrollView
              style={{ maxHeight: 200 }}
              nestedScrollEnabled
              keyboardShouldPersistTaps='handled'
              contentContainerStyle={{ flexGrow: 1, alignContent: 'center' }}>
              {allUsers.map((user: UserInstance) => (
                <Pressable
                  key={user.email}
                  onPress={() => {
                    const newFilters = cloneFilters(draftFilters);
                    newFilters[index].value = user.email;
                    newFilters[index].type = 'Email';
                    newFilters[index].id = user.id;
                    setDraftFilters(newFilters);
                    setFilterDropdownIndex(null);
                  }}
                >
                  <Text style={styles.dropdownBoxText}>{user.email}</Text>
                </Pressable>
              ))}
            </ScrollView>
          </View>
        );
      default:
        return null;
    }
  };

  // eslint-disable-next-line no-unused-vars
  const renderSortDropdown = () => (
    <View style={[styles.dropdownView, isMobile ? { width: '100%', top: 50 } : {}]}>
      <Pressable
        style={(state) => [{ backgroundColor: state.hovered ? Colors.light.hereworksPaleBlue : '#fff' }]}
        onPressOut={() => {
          setSortDropdownSelection('by alphabet A-Z');
          setSortDropdownVisible(!sortDropdownVisible);
        }}
      >
        <Text style={styles.dropdownItemText}>by alphabet A-Z</Text>
      </Pressable>
      <Pressable
        style={(state) => [{ backgroundColor: state.hovered ? Colors.light.hereworksPaleBlue : '#fff' }]}
        onPressOut={() => {
          setSortDropdownSelection('by alphabet Z-A');
          setSortDropdownVisible(!sortDropdownVisible);
        }}
      >
        <Text style={styles.dropdownItemText}>by alphabet Z-A</Text>
      </Pressable>
    </View>
  );

  const renderSearchDropdown = () => (
    <View style={[styles.dropdownView, isMobile ? { width: '100%', top: 50 } : {}]}>
      <Pressable
        style={(state) => [{ backgroundColor: state.hovered ? Colors.light.hereworksPaleBlue : '#fff' }]}
        onPressOut={() => {
          setSearchDropdownSelection('Users');
          setSearchDropdownVisible(!searchDropdownVisible);
        }}
        testID='search-users'
      >
        <Text style={styles.dropdownItemText}>Users</Text>
      </Pressable>
      <Pressable
        style={(state) => [{ backgroundColor: state.hovered ? Colors.light.hereworksPaleBlue : '#fff' }]}
        onPressOut={() => {
          setSearchDropdownSelection('Groups');
          setSearchDropdownVisible(!searchDropdownVisible);
        }}
        testID='search-groups'
      >
        <Text style={styles.dropdownItemText}>Groups</Text>
      </Pressable>
    </View>
  );

  const renderAddButtons = () => (
    <View
      style={{
        flexDirection: 'row',
        alignSelf: 'stretch',
        justifyContent: 'flex-end',
        marginHorizontal: isMobile ? 24 : 40,
      }}
    >
      {!isMobile && (
        <View style={{ flex: 1, flexDirection: 'row' }}>
          <Text
            style={{
              fontSize: 22,
              lineHeight: 33,
              fontFamily: 'Poppins_400Regular',
              color: selectedSection
                ? Colors.light.hereworksBlack600
                : Colors.light.hereworksBlack300,
            }}
          >
            User management
          </Text>
        </View>
      )}
      {canAddUserGroups && <Pressable
        style={[
          styles.buttonCommon,
          {
            borderColor: Colors.light.hereworksBlue500,
            backgroundColor: Colors.light.hereworksBlue500,
            flex: isMobile ? 1 : null,
            paddingHorizontal: isMobile ? 0 : 24,
          },
        ]}
        onPress={() => {
          setAddGroupsModalVisible(!addGroupsModalVisible);
          logEvent('UserMgmt', 'Add_groups');
        }}
        testID='add-user-group-btn'
      >
        <Text
          style={{
            color: '#fff',
            fontFamily: 'Roboto_400Regular',
            fontSize: 16,
            lineHeight: 24,
            marginVertical: 8,
            marginRight: 8,
          }}
          numberOfLines={1}
        >
          Add groups
        </Text>
        <EditIcon color={'#fff'} />
      </Pressable>}
      {canAddUsers && <Pressable
        style={[
          styles.buttonCommon,
          {
            borderColor: Colors.light.hereworksLightBlue,
            backgroundColor: '#fff',
            marginLeft: 24,
            flex: isMobile ? 1 : null,
            paddingHorizontal: isMobile ? 0 : 24,
          },
        ]}
        onPress={() => {
          setInviteModalVisible(true);
          userInviteBottomSheetRef?.current?.present();
          logEvent('UserMgmt', 'Invite_users');
        }}
        testID='invite-users-btn'
      >
        <Text
          style={{
            color: Colors.light.hereworksLightBlue,
            fontFamily: 'Roboto_400Regular',
            fontSize: isMobile ? 16 : 18,
            lineHeight: isMobile ? 24 : 26,
            marginVertical: 8,
            marginRight: 8,
          }}
          numberOfLines={1}
        >
          Invite users
        </Text>
        <UserIcon16 />
      </Pressable>}
    </View>
  );

  const RenderGroupsWeb = () => canViewUserGroups && <View
    style={{ zIndex: 1, alignSelf: 'stretch' }}
    testID='user-group-list'
  >
    {loading && (
      <ActivityIndicator
        color={Colors.light.hereworksLoading}
        size={'large'}
        style={{ marginTop: 40 }}
      />
    )}
    {userGroups.length ? userGroups
      .filter((group) => {
        let compareValue = group.Name;
        if (searchDropdownSelection === 'Groups') {
          compareValue = searchValue ?? group.Name;
        }
        return group.Name?.toLowerCase().includes(compareValue.toLowerCase());
      })
      .sort((a, b) => {
        if (sortDropdownSelection === 'by alphabet A-Z') {
          return a.Name.localeCompare(b.Name);
        }
        return b.Name.localeCompare(a.Name);
      })
      .map((group: UserGroup, index: number) => (
        <Animatable.View
          key={`${group.UserGroupId}`}
          style={{ alignSelf: 'stretch', zIndex: 1 }}
          testID={`user-group#${index + 1}`}
        >
          <UserGroupCard
            title={group.Name}
            description={group.Description}
            groupId={group.UserGroupId}
            userCount={group.UsersCount}
            roles={roles}
            drawerOpen={!!openedGroupCards[group.UserGroupId]}
            onDrawerOpenChange={(isOpen: boolean) => {
              setOpenedGroupCards((prev) => ({
                ...prev,
                [group.UserGroupId]: isOpen,
              }));
            }}
            allUsers={allUsers
              .filter((user: UserInstance) => user.status === UserStatus.VERIFIED)}
            updateUsers={() => setUpdateUsers(!updateUsers)}
            searchValue={searchDropdownSelection === 'Users' ? searchValue : ''}
            deletedGroup={() => {
              setAddGroupsModalVisible(false);
              setLoading(true);
              showAlert(DELETE_USER_GROUP_SUCCESS, '', false);
            }}
          />
        </Animatable.View>
      )) : <ScreenEmptyState
      image={<NoUsersImage />}
      title='No user group yet'
      subtitle={isAdmin ? 'Add your first user group' : 'Please ask your administrator to create a user group for you'}
      buttonTitle='Create Group'
      buttonAction={() => setAddGroupsModalVisible(true)}
    />}
  </View>;

  const RenderGroupsMobile = () => canViewUserGroups && <View
    style={{ zIndex: 1, alignSelf: 'stretch' }}
    testID='user-group-list'
  >
    {loading && (
      <ActivityIndicator
        color={Colors.light.hereworksLoading}
        size={'large'}
        style={{ marginTop: 40 }}
      />
    )}
    {userGroups.length ? userGroups
      .filter((group) => {
        let compareValue = group.Name;
        if (searchDropdownSelection === 'Groups') {
          compareValue = searchValue ?? group.Name;
        }
        return group.Name?.toLowerCase().includes(compareValue.toLowerCase());
      })
      .sort((a, b) => {
        if (sortDropdownSelection === 'by alphabet A-Z') {
          return a.Name.localeCompare(b.Name);
        }
        return b.Name.localeCompare(a.Name);
      })
      .map((group: UserGroup, index: number) => (
        <View
          key={`${group.UserGroupId}`}
          style={{ alignSelf: 'stretch', zIndex: 1 }}
          testID={`user-group#${index + 1}`}
        >
          <UserGroupCardMobile
            group={group}
            title={group.Name}
            description={group.Description}
            setSelectedUserGroup={() => {
              setShowSectionDropdown(false);
              setSelectedUserGroup(group);
            }}
          />
        </View>
      )) : <ScreenEmptyState
      image={<NoUsersImage />}
      title='No user group yet'
      subtitle={isAdmin ? 'Add your first user group' : 'Please ask your administrator to create a user group for you'}
      buttonTitle='Create Group'
      buttonAction={() => setAddGroupsModalVisible(true)}
    />}
  </View>;

  const RenderUsers = () => canViewUsers
    && <View style={{ marginHorizontal: isMobile ? 24 : 40, marginBottom: 20 }}>
      <Text
        style={{
          fontFamily: 'Poppins_400Regular',
          fontSize: 22,
          lineHeight: 33,
          color: Colors.light.hereworksBlack600,
          marginTop: 40,
          marginBottom: 24,
        }}
      >
        Users
      </Text>
      <View style={{
        backgroundColor: '#fff',
        borderRadius: 10,
        borderWidth: 1,
        paddingHorizontal: 24,
        borderColor: Colors.light.hereworksWhite300,
      }}>
        <View style={{ flexDirection: 'row' }}>
          <Text
            style={{
              fontFamily: 'Poppins_400Regular',
              fontSize: 22,
              lineHeight: 33,
              color: Colors.light.hereworksBlack600,
              marginTop: 18,
              marginBottom: 24,
            }}
          >
            {availableUsersSelected ? 'Available users' : 'Invitation sent'}
          </Text>
          <View style={{
            flexDirection: 'row',
            flex: 1,
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}>
            <Pressable onPress={() => {
              setAvailableUsersSelected(true);
            }}
              style={{
                paddingVertical: 4,
                paddingHorizontal: 12,
                borderRadius: 4,
                backgroundColor:
                  !availableUsersSelected ? '#fff' : Colors.light.hereworksBlue500,
                borderWidth: 1,
                borderColor:
                  !availableUsersSelected
                    ? Colors.light.hereworksBlue300
                    : Colors.light.hereworksBlue500,
                marginRight: 8,
              }}
            >
              <Text style={{
                fontFamily: 'Roboto_500Medium',
                fontSize: 12,
                lineHeight: 20,
                color: !availableUsersSelected ? Colors.light.hereworksBlue400 : '#fff',
              }}>
                AVAILABLE USERS
              </Text>
            </Pressable>
            <Pressable
              onPress={() => setAvailableUsersSelected(false)}
              style={{
                paddingVertical: 4,
                paddingHorizontal: 12,
                borderRadius: 4,
                backgroundColor:
                  availableUsersSelected ? '#fff' : Colors.light.hereworksBlue500,
                borderWidth: 1,
                borderColor:
                  availableUsersSelected
                    ? Colors.light.hereworksBlue300
                    : Colors.light.hereworksBlue500,
              }}
            >
              <Text style={{
                fontFamily: 'Roboto_500Medium',
                fontSize: 12,
                lineHeight: 20,
                color: availableUsersSelected ? Colors.light.hereworksBlue400 : '#fff',
              }}>
                INVITATION SENT
              </Text>
            </Pressable>
          </View>
        </View>
        <View
          style={{
            flexDirection: 'row',
            marginVertical: 12,
            alignSelf: 'stretch',
          }}
        >
          <Text style={[styles.headerText, { marginLeft: 50, flex: 1 }]}>USER</Text>
          <Text style={[styles.headerText, { marginLeft: 40, flex: 1 }]}>CONTACTS</Text>
          <Text style={[styles.headerText, { marginLeft: 10, flex: 1 }]}>ROLE</Text>
          <Text style={[styles.headerText]}>STATUS</Text>
          <View style={{ flex: 1 }}>
            <Text
              style={[
                styles.headerText,
                {
                  alignSelf: 'flex-end',
                  marginRight: 28,
                },
              ]}
            >
              {(canUpdateUsers || canDeleteUsers) ? 'ACTIONS' : 'LAST ACTIVITY'}
            </Text>
          </View>
        </View>
        <View
          style={{
            backgroundColor: '#fff',
            paddingHorizontal: 24,
            borderRadius: 10,
            borderWidth: 1,
            borderColor: Colors.light.hereworksWhite300,
            paddingBottom: 24,
          }}
          testID='users-list-in-user-group'
        >
          {!loading && (allUsers?.length > 0 ? allUsers
            ?.map((user) => {
              const modifiedUser = { ...user };
              if (!(user?.FullName?.length > 0)) {
                modifiedUser.FullName = getUserFullName(user);
              }
              return modifiedUser;
            })
            ?.filter((group) => {
              let compareValue = group.FullName;
              if (searchDropdownSelection === 'Users') {
                compareValue = searchValue ?? group.FullName;
              }
              return (
                !(searchValue || searchValue?.length)
                || `${group?.firstName ?? ''} ${group?.lastName ?? ''}`.trim().toLowerCase()
                  .includes(compareValue.trim().toLowerCase())
                || group.FullName?.toLowerCase().includes(compareValue.trim().toLowerCase())
              );
            })
            ?.sort((a, b) => {
              if (sortDropdownSelection === 'by alphabet Z-A') {
                return b.FullName.localeCompare(a.FullName);
              }
              return a.FullName.localeCompare(b.FullName);
            })
            ?.map((user, key) => {
              if (availableUsersSelected === true && user.status === 'PENDING_VERIFICATION') {
                return null;
              }
              if (!availableUsersSelected && user.status !== 'PENDING_VERIFICATION') {
                return null;
              }
              return (
                <View
                  key={key}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    borderBottomWidth: 1,
                    borderColor: Colors.light.hereworksWhite500,
                    paddingBottom: 4,
                  }}
                >
                  <Text>{key + 1}</Text>
                  <UserRowWeb
                    {...{ user }}
                    userDeleted={() => {
                      showAlert(DELETE_USER_SUCCESS, '', false);
                      setUpdateUsers(!updateUsers);
                    }}
                    showError={(message) => {
                      showAlert('Error', message ?? SOMETHING_WENT_WRONG, true);
                    }}
                    roleChanged={() => {
                      setUpdateUsers(!updateUsers);
                    }}
                    roles={roles ?? []}
                    userGroups={userGroups}
                  />
                </View>);
            }) : <ScreenEmptyState
            image={<NoUsersImage />}
            title={availableUsersSelected ? 'No users invited yet' : 'No invitations sent'}
            subtitle='Get started by inviting users to join your community'
            buttonTitle='Invite Users'
            actionAvailable
            buttonAction={() => setInviteModalVisible(true)}
          />)}
        </View>
      </View>
    </View>;

  const Tabs = ({
    titleOne, titleTwo, selected, setSelected,
  }) => (
    <View style={{
      flexDirection: 'row',
      marginBottom: 16,
      marginHorizontal: 0,
      borderBottomWidth: 1,
      borderColor: Colors.light.hereworksBlue100,
    }}>
      <Pressable onPress={() => setSelected(true)}
        style={{
          paddingVertical: 8,
          paddingHorizontal: 16,
          borderBottomWidth: 4,
          borderColor: selected
            ? Colors.light.hereworksBlue500 : 'transparent',
          marginRight: 8,
        }}
      >
        <Text style={{
          fontFamily: 'Roboto_500Medium',
          fontSize: 12,
          lineHeight: 20,
          color: selected
            ? Colors.light.hereworksBlue500 : Colors.light.hereworksBlack500,
        }}>
          {titleOne}
        </Text>
      </Pressable>
      <Pressable onPress={() => setSelected(false)}
        style={{
          paddingVertical: 8,
          paddingHorizontal: 16,
          borderBottomWidth: 4,
          borderColor: !selected
            ? Colors.light.hereworksBlue500 : 'transparent',
        }}
      >
        <Text style={{
          fontFamily: 'Roboto_500Medium',
          fontSize: 12,
          lineHeight: 20,
          color: !selected
            ? Colors.light.hereworksBlue500 : Colors.light.hereworksBlack500,
        }}>
          {titleTwo}
        </Text>
      </Pressable>
    </View>
  );

  const updatedGroup = (groupId: string, title: string, description: string, count: number) => {
    userGroups.forEach((group: UserGroup) => {
      if (group.UserGroupId === groupId) {
        group.Name = title;
        group.Description = description;
        group.UsersCount = count;
      }
    });
    setAddGroupsModalVisible(false);
    setEditUserGroup(false);
    setLoading(true);
    setUpdateUsers(!updateUsers);
    showAlert(SAVE_USER_GROUP_SUCCESS, '', false, null, '', 'Edit group');
  };

  return canViewUserGroups || canViewUsers ? (
    <View style={styles.container}>
      <GroupEditModal
        modalVisible={addGroupsModalVisible}
        setModalVisible={setAddGroupsModalVisible}
        updatedGroup={updatedGroup}
        users={allUsers.filter(
          (user: UserInstance) => user.status === UserStatus.VERIFIED,
        )}
        title={undefined}
        description={undefined}
        groupId={undefined}
        groupDeleted={() => {
          setAddGroupsModalVisible(false);
        }}
        notificationGroups={[]}
      />
      {!isMobile && <InviteUserModal
        modalVisible={inviteModalVisible}
        setModalVisible={setInviteModalVisible}
        users={allUsers.filter(
          (user: UserInstance) => user.status === UserStatus.VERIFIED,
        )}
        invitedUser={() => {
          setInviteModalVisible(false);
          setUpdateUsers(!updateUsers);
          showAlert(SEND_INVITE_SUCCESS, '', false);
        }}
        roles={roles}
      />}
      {!isMobile && renderAddButtons()}
      {isMobile && (
        <ScrollView
          style={{ width: '100%' }}
          contentContainerStyle={styles.scrollContainer}
        >
          <View style={{ marginBottom: 8, alignSelf: 'stretch', zIndex: 5 }}>
            <Text
              style={{
                marginLeft: 24,
                marginTop: 12,
                fontSize: 24,
                fontFamily: 'Roboto_500Medium',
                color: Colors.light.hereworksBlack600,
                alignSelf: 'flex-start',
              }}
            >
              User management
            </Text>
            {isDev && (
              <Pressable
                style={(state) => [{
                  flexDirection: 'row',
                  alignSelf: 'stretch',
                  alignItems: 'center',
                  paddingVertical: 12,
                  marginTop: 8,
                  backgroundColor: state.pressed
                    ? Colors.light.hereworksBlue100 : Colors.light.hereworksBlue50,
                  borderRadius: 10,
                  marginHorizontal: 24,
                  borderBottomWidth: 1,
                  borderColor: Colors.light.hereworksWhite300,
                }]}
                onPress={() => {
                  setShowSectionDropdown(!showSectionDropdown);
                }}
              >
                {
                  {
                    groups: <ListIcon16
                      color={Colors.light.hereworksBlue500}
                      style={{ marginVertical: 9, marginLeft: 12 }} />,
                    users: <UserIcon16 style={{ marginVertical: 9, marginLeft: 12 }} />,
                    roles: <UserGroupIcon16 style={{ marginVertical: 9, marginLeft: 12 }} />,
                  }[selectedSection]
                }
                <Text
                  style={{
                    fontSize: 14,
                    marginLeft: 12,
                    fontFamily: 'Roboto_500Medium',
                    color: Colors.light.hereworksBlue500,
                    flex: 1,
                  }}
                >
                  {
                    {
                      users: 'Users',
                      groups: 'Groups',
                      roles: 'Roles',
                    }[selectedSection]
                  }
                </Text>
                <TriangleDown
                  color={Colors.light.hereworksBlue500}
                  style={{
                    marginRight: 12,
                    transform: [{ rotate: showSectionDropdown ? '180deg' : '0deg' }],
                  }} />
              </Pressable>
            )}
            {showSectionDropdown && isDev && (
              <View
                style={{
                  position: 'absolute',
                  top: 110,
                  left: 24,
                  right: 24,
                  backgroundColor: '#fff',
                  borderWidth: 1,
                  borderColor: Colors.light.hereworksBlue100,
                  overflow: 'hidden',
                  borderRadius: 10,
                  zIndex: 10,
                }}
              >
                {(
                  ['users', 'groups', 'roles'] as (
                    | 'groups'
                    | 'users'
                    | 'roles'
                  )[]
                ).map((section) => (
                  <Pressable
                    key={section}
                    style={(state) => {
                      let backgroundColor = '#fff';
                      if (state.pressed) {
                        backgroundColor = Colors.light.hereworksBlue100;
                      } else if (state.hovered) {
                        backgroundColor = Colors.light.hereworksWhite500;
                      }
                      return {
                        backgroundColor,
                        flexDirection: 'row',
                        alignItems: 'center',
                        paddingVertical: 6,
                      };
                    }}
                    onPress={() => {
                      setSelectedSection(section);
                      setShowSectionDropdown(false);
                    }}
                  >
                    {
                      {
                        groups: <ListIcon16
                          color={Colors.light.hereworksBlue500}
                          style={{ marginLeft: 12 }} />,
                        users: <UserIcon16 style={{ marginLeft: 12 }} />,
                        roles: <UserGroupIcon16 style={{ marginLeft: 12 }} />,
                      }[section]
                    }
                    <Text
                      style={{
                        fontSize: 14,
                        fontFamily: 'Roboto_500Medium',
                        color: Colors.light.hereworksBlack600,
                        marginVertical: 9,
                        marginHorizontal: 12,
                      }}
                    >
                      {
                        {
                          groups: 'Groups',
                          users: 'Users',
                          roles: 'Roles',
                        }[section]
                      }
                    </Text>
                  </Pressable>
                ))}
              </View>
            )}
          </View>
          {selectedSection === 'groups' && (
            <View style={{ marginBottom: 40 }}>
              {!isDev && renderAddButtons()}
              <Pressable
                style={[
                  styles.dropdownBox,
                  {
                    zIndex: 1,
                    marginHorizontal: isMobile ? 24 : 40,
                    marginTop: 10,
                    borderRadius: 4,
                    borderColor: searchFocused
                      ? Colors.light.hereworksBlue500
                      : Colors.light.hereworksBlue50,
                  },
                ]}
              >
                <View style={{ alignItems: 'flex-start', marginLeft: 12 }}>
                  <SearchIcon24 />
                </View>
                <TextInput
                  style={styles.dropdownBoxText}
                  placeholder={'Search'}
                  placeholderTextColor={Colors.light.hereworksBlack400}
                  onChangeText={(text) => {
                    setSearchValue(text);
                  }}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setSearchFocused(false)}
                />
              </Pressable>
              <RenderGroupsMobile />
            </View>
          )}
          {selectedSection === 'users' && (
            <View style={{ marginBottom: 40, paddingHorizontal: 24 }}>
              <Tabs
                titleOne="ACTIVATED USERS"
                titleTwo="INVITED USERS"
                selected={availableUsersSelected}
                setSelected={setAvailableUsersSelected}
              />
              <View style={Style.row}>
                <SearchBar apply={setSearchValue} loading={false} placeholder={'Search'} />
                <PressableStyled
                  style={{
                    marginLeft: 8,
                    borderWidth: 1,
                    borderColor: Colors.light.hereworksBlue100,
                    borderRadius: 4,
                    padding: 8,
                  }}
                  onPress={() => {
                    openUserFilterMenu();
                    logEvent('UserMgmt', 'open_user_filter_mobile');
                  }}
                  testID='user-filter-btn'
                >
                  <FilterIconMobile16 />
                  {(appliedSortEnabled || appliedFilters.length > 0)
                    && <View style={styles.indicationDot} />}
                </PressableStyled>
              </View>
              {allUsers.filter((user) => {
                if (searchValue && searchValue.length > 0) {
                  const fullName: string | undefined = getUserFullName(user);
                  if (!fullName?.toLowerCase().includes(searchValue.toLowerCase())
                    && !(user.email.toLowerCase().includes(searchValue.toLowerCase()))) {
                    return false;
                  }
                }
                if (availableUsersSelected && user.status === UserStatus.VERIFIED) {
                  return true;
                }
                if (!availableUsersSelected && user.status === UserStatus.PENDING_VERIFICATION) {
                  return true;
                }
                return false;
              }).map((user) => {
                const modifiedUser = { ...user };
                if (!(user?.FullName?.length > 0)) {
                  modifiedUser.FullName = getUserFullName(user);
                }
                return modifiedUser;
              }).sort((a, b) => {
                if (sortDropdownSelection === 'by alphabet A-Z') {
                  return a.FullName.localeCompare(b.FullName);
                }
                return b.FullName.localeCompare(a.FullName);
              })
                .map((user, key) => (
                  <View
                    key={key}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <UserCardMobile
                      user={user}
                      roles={roles}
                      canUpdateUsers
                      canDeleteUsers
                      hideSectionDropdown={() => { setShowSectionDropdown(false); }}
                      deleteAction={() => deleteUser(user.email).then((res) => {
                        if (res?.isError) {
                          showAlert('Error', res.message || SOMETHING_WENT_WRONG, true);
                        } else {
                          setUpdateUsers(!updateUsers);
                          showAlert(DELETE_USER_SUCCESS, '', false);
                        }
                      })}
                      userGroups={userGroups}
                      updateUsers={updateUsers}
                      setUpdateUsers={setUpdateUsers}
                    />
                  </View>
                ))}
              {allUsers.filter((user) => {
                if (searchValue && searchValue.length > 0) {
                  const fullName: string | undefined = getUserFullName(user);
                  if (!fullName?.toLowerCase().includes(searchValue.toLowerCase())
                    && !(user.email.toLowerCase().includes(searchValue.toLowerCase()))) {
                    return false;
                  }
                }
                if (availableUsersSelected && user.status === UserStatus.VERIFIED) {
                  return true;
                }
                if (!availableUsersSelected && user.status === UserStatus.PENDING_VERIFICATION) {
                  return true;
                }
                return false;
              }).sort((a, b) => {
                if (sortDropdownSelection === 'by alphabet A-Z') {
                  return a.email.localeCompare(b.email);
                }
                return b.email.localeCompare(a.email);
              }).length === 0 && <Text style={{
                textAlign: 'center',
                marginTop: 20,
                color: Colors.light.hereworksBlack400,
                fontFamily: 'Poppins_400Regular',
              }}>
                  No users found
                </Text>}
            </View>
          )}
          {selectedSection === 'roles' && (
            <View style={{ marginBottom: 40, marginHorizontal: 24, flex: 1 }}>
              {roles.map((role: any, index: number) => (
                <Pressable
                  style={(state) => [{
                    backgroundColor: '#fff',
                    borderRadius: 10,
                    borderWidth: 1,
                    borderColor: Colors.light.hereworksWhite300,
                    marginBottom: 12,
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingRight: 8,
                    opacity: state.pressed ? 0.5 : 1,
                  }]}
                  key={index}
                  onPress={() => {
                    setShowSectionDropdown(false);
                    setShowRoleMenu(false);
                    setSelectedRole(role);
                  }}
                >
                  <View style={{ flex: 1 }}>
                    <Text
                      style={{
                        fontFamily: 'Poppins_600SemiBold',
                        fontSize: 16,
                        lineHeight: 24,
                        color: Colors.light.hereworksBlack600,
                        marginTop: 9,
                        marginBottom: 4,
                        marginHorizontal: 12,
                      }}
                    >
                      {role.name}
                    </Text>
                    <Text
                      style={{
                        fontFamily: 'Poppins_400Regular',
                        fontSize: 12,
                        lineHeight: 14,
                        color: Colors.light.hereworksBlack400,
                        marginHorizontal: 12,
                        marginBottom: 12,
                      }}
                    >
                      {role.assignedUsersCount} members
                    </Text>
                  </View>
                  <RightArrow />
                </Pressable>
              ))}
            </View>
          )}
          <BottomSheetModal
            ref={userFilterBottomSheetRef}
            index={0}
            snapPoints={['85%']}
            backgroundStyle={{ borderRadius: 10 }}
            onDismiss={() => {
              resetFilterDraftState();
            }}
            handleIndicatorStyle={{ backgroundColor: 'transparent' }}
            enableContentPanningGesture={false}
            enablePanDownToClose={false}
            backdropComponent={(props) => (
              <BottomSheetBackdrop
                {...props}
                disappearsOnIndex={-1}
                appearsOnIndex={0}
                pressBehavior={'none'}
              />)}>
            <BottomSheetScrollView
              contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 30 }}
              keyboardShouldPersistTaps='handled'
              nestedScrollEnabled
            >
              <Text style={{
                fontFamily: 'Poppins_600SemiBold',
                fontSize: 16,
                color: Colors.light.hereworksBlack600,
                textAlign: 'center',
              }}>
                Filter & Sort
              </Text>
              <Text style={{
                fontFamily: 'Poppins_400Regular',
                fontSize: 18,
                color: Colors.light.hereworksBlack600,
                marginBottom: 16,
              }}>Filter</Text>
              {draftFilters.map((filter, index) => (
                <View key={index} style={{
                  marginBottom: 16,
                  borderWidth: 1,
                  borderColor: Colors.light.hereworksWhite300,
                  borderRadius: 4,
                  padding: 12,
                }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Pressable
                      style={[Style.dropdown, { width: 200, paddingHorizontal: 12 }]}
                      onPress={() => {
                        if (filterItemDropdownIndex === index) {
                          setFilterItemDropdownIndex(null);
                          return;
                        }
                        setFilterItemDropdownIndex(index);
                      }}
                    >
                      <Text style={{
                        fontFamily: 'Poppins_400Regular',
                        fontSize: 14,
                        flex: 1,
                        color: Colors.light.hereworksBlack600,
                      }}>{filter.type}</Text>
                      <FontAwesome
                        name={filterItemDropdownIndex === index ? 'angle-up' : 'angle-down'}
                        size={24}
                        color={Colors.light.hereworksBlue500}
                      />
                    </Pressable>
                    <Pressable
                      onPress={() => {
                        const newFilters = cloneFilters(draftFilters);
                        newFilters.splice(index, 1);
                        setFilterEnabled(newFilters.length > 0);
                        setDraftFilters(newFilters);
                      }}
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                      }}
                    >
                      <DeleteIcon24 />
                    </Pressable>
                  </View>
                  {filterItemDropdownIndex === index && (
                    <View style={[styles.dropdownBox, { alignSelf: 'stretch', width: 200 }]}>
                      <View>
                        <PressableStyled
                          disabled={filter.type === 'Name' || draftFilters.find((f, i) => f.type === 'Name' && i !== index) !== undefined}
                          onPress={() => {
                            const newFilters = cloneFilters(draftFilters);
                            newFilters[index] = { type: 'Name', value: '' };
                            setDraftFilters(newFilters);
                            setFilterItemDropdownIndex(null);
                            setFilterDropdownIndex(null);
                          }}
                        >
                          <Text style={styles.dropdownBoxText}>Name</Text>
                        </PressableStyled>
                        <PressableStyled
                          disabled={filter.type === 'Role' || draftFilters.find((f, i) => f.type === 'Role' && i !== index) !== undefined}
                          onPress={() => {
                            const newFilters = cloneFilters(draftFilters);
                            newFilters[index] = { type: 'Role', value: 'All Roles' };
                            setDraftFilters(newFilters);
                            setFilterItemDropdownIndex(null);
                            setFilterDropdownIndex(null);
                          }}
                        >
                          <Text style={styles.dropdownBoxText}>Role</Text>
                        </PressableStyled>
                        <PressableStyled
                          disabled={filter.type === 'Email' || draftFilters.find((f, i) => f.type === 'Email' && i !== index) !== undefined}
                          onPress={() => {
                            const newFilters = cloneFilters(draftFilters);
                            newFilters[index] = { type: 'Email', value: 'All Emails' };
                            setDraftFilters(newFilters);
                            setFilterItemDropdownIndex(null);
                            setFilterDropdownIndex(null);
                          }}
                        >
                          <Text style={styles.dropdownBoxText}>Email</Text>
                        </PressableStyled>
                      </View>
                    </View>
                  )}
                  {filter.type === 'Name'
                    ? <View style={[styles.dropdownBox, { alignSelf: 'stretch' }]}>
                      <CustomTextField
                        placeholder='Enter name'
                        value={filter.value}
                        updateValue={(text) => {
                          const newFilters = cloneFilters(draftFilters);
                          newFilters[index].value = text;
                          newFilters[index].type = 'Name';
                          setDraftFilters(newFilters);
                        }}
                      />
                    </View> : <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <Pressable
                        onPress={() => {
                          setFilterDropdownIndex(index);
                        }}
                        style={[styles.dropdownBox, {
                          alignSelf: 'stretch', width: 200, marginTop: 2,
                        }]}
                      >
                        <Text style={styles.dropdownBoxText}>{filter.value}</Text>
                      </Pressable>
                    </View>}
                  {filterDropdownIndex === index && (
                    renderFilterDropdown(filter, index)
                  )}
                </View>
              ))}
              {(draftFilters.find((f) => f.type === 'Role') === undefined
                || draftFilters.find((f) => f.type === 'Name') === undefined
                || draftFilters.find((f) => f.type === 'Email') === undefined)
                && <Pressable style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 20 }}
                  onPress={() => {
                    logEvent('UserMgmt', 'add_user_filter_mobile');
                    setFilterDropdownVisible(!filterDropdownVisible);
                    setFilterEnabled(true);
                    if (draftFilters.find((f) => f.type === 'Name') === undefined) {
                      setDraftFilters([...draftFilters, { type: 'Name', value: '' }]);
                      return;
                    }
                    if (draftFilters.find((f) => f.type === 'Role') === undefined) {
                      setDraftFilters([...draftFilters, { type: 'Role', value: 'All Roles' }]);
                      return;
                    }
                    setDraftFilters([...draftFilters, { type: 'Email', value: 'All Emails' }]);
                  }}>
                  <PlusIcon16 />
                  <Text style={{
                    fontFamily: 'Poppins_400Regular',
                    fontSize: 14,
                    color: Colors.light.hereworksBlue500,
                    marginLeft: 8,
                  }}>Add filter</Text>
                </Pressable>}
              <Text style={{
                fontFamily: 'Poppins_400Regular',
                fontSize: 18,
                color: Colors.light.hereworksBlack600,
                marginBottom: 16,
              }}>Sort</Text>
              {!draftSortEnabled ? <Pressable style={{ flexDirection: 'row', alignItems: 'center' }}
                onPress={() => {
                  setDraftSortEnabled(true);
                }}>
                <PlusIcon16 />
                <Text style={{
                  fontFamily: 'Poppins_400Regular',
                  fontSize: 14,
                  color: Colors.light.hereworksBlue500,
                  marginLeft: 8,
                }}>Add sort</Text>
              </Pressable> : (
                <View>
                  <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
                    <Text style={{
                      fontFamily: 'Poppins_400Regular',
                      fontSize: 14,
                      color: Colors.light.hereworksBlack600,
                    }}>Sort</Text>
                    <Pressable
                      onPress={() => setSortDropdownVisible(!sortDropdownVisible)}
                      style={{ marginHorizontal: 8 }}
                    >
                      <Text style={{
                        fontFamily: 'Poppins_400Regular',
                        fontSize: 14,
                        color: Colors.light.hereworksBlue500,
                      }}>{draftSortDropdownSelection}</Text>
                    </Pressable>
                  </View>
                  {sortDropdownVisible && <View style={[styles.dropdownBox, { alignSelf: 'stretch' }]}>
                    <View>
                      <Pressable
                        onPress={() => {
                          setDraftSortDropdownSelection('by alphabet A-Z');
                          setSortDropdownVisible(false);
                        }}
                        style={{ marginBottom: 8, padding: 10 }}
                      >
                        <Text style={{
                          fontFamily: 'Poppins_400Regular',
                          fontSize: 14,
                          color: Colors.light.hereworksBlack600,
                        }}>by alphabet A-Z</Text>
                      </Pressable>
                      <Pressable
                        onPress={() => {
                          setDraftSortDropdownSelection('by alphabet Z-A');
                          setSortDropdownVisible(false);
                        }}
                        style={{ marginBottom: 8, padding: 10 }}
                      >
                        <Text style={{
                          fontFamily: 'Poppins_400Regular',
                          fontSize: 14,
                          color: Colors.light.hereworksBlack600,
                        }}>by alphabet Z-A</Text>
                      </Pressable>
                    </View>
                  </View>}
                </View>
              )}
              {(draftSortEnabled || filterEnabled) && (
                <Button
                  onPress={() => {
                    setDraftSortEnabled(false);
                    setFilterEnabled(false);
                    setDraftFilters([]);
                    setDraftSortDropdownSelection('by alphabet A-Z');
                  }}
                  style={{ alignSelf: 'stretch', marginTop: 16 }}
                  title='Reset all'
                />
              )}
              <Button
                onPress={() => {
                  setAppliedFilters(cloneFilters(draftFilters));
                  setAppliedSortEnabled(draftSortEnabled);
                  setAppliedSortDropdownSelection(draftSortDropdownSelection);
                  setSortEnabled(draftSortEnabled);
                  setSortDropdownSelection(draftSortDropdownSelection);
                  userFilterBottomSheetRef.current?.dismiss();
                  setUpdateUsers(!updateUsers);
                  logEvent('UserFilterDrawer', 'Apply_Filters_Modal');
                }}
                style={{ alignSelf: 'stretch', marginTop: 16, marginStart: 0 }}
                affirmative
                title='Apply'
              />
              <Pressable
                onPress={() => {
                  logEvent('UserFilterDrawer', 'Close_Modal');
                  resetFilterDraftState();
                  closeUserFilterMenu();
                }}
                style={{ position: 'absolute', top: 0, right: 16 }}
              >
                <CloseModalIcon />
              </Pressable>
            </BottomSheetScrollView>
          </BottomSheetModal>
          <BottomSheetModal
            ref={userInviteBottomSheetRef}
            index={1}
            keyboardBehavior='interactive'
            snapPoints={['50%']}
            backgroundStyle={{ borderRadius: 10 }}
            onDismiss={() => userInviteBottomSheetRef.current?.dismiss()}
            handleIndicatorStyle={{ backgroundColor: 'transparent' }}
            enableContentPanningGesture={false}
            backdropComponent={(props) => (
              <BottomSheetBackdrop
                {...props}
                disappearsOnIndex={-1}
                appearsOnIndex={0}
              />)}>
            <BottomSheetView style={{
              paddingHorizontal: 20,
              flex: 1,
              paddingBottom: 40,
            }}>
              <InviteUserBottomSheet
                users={allUsers.filter(
                  (user: UserInstance) => user.status === UserStatus.VERIFIED,
                )}
                invitedUser={() => {
                  // userInviteBottomSheetRef.current?.dismiss();
                  setUpdateUsers(!updateUsers);
                  // showAlert(SEND_INVITE_SUCCESS, '', false);
                }}
                roles={roles}
                close={() => userInviteBottomSheetRef.current?.dismiss()}
              />
            </BottomSheetView>
          </BottomSheetModal>
        </ScrollView>
      )}
      {!isMobile && selectedSection === 'groups' && (
        <View
          style={{
            alignSelf: 'stretch',
            backgroundColor: Colors.light.hereworksBackground,
            flex: 1,
          }}
        >
          <View
            style={{ flexDirection: 'row', alignSelf: 'stretch', zIndex: 4 }}
          >
            <View
              style={[
                styles.dropdownBox,
                { flex: 1, marginHorizontal: isMobile ? 24 : 40 },
              ]}
            >
              <Pressable
                style={{ flexDirection: 'row', alignItems: 'center' }}
                testID="um-search-by-drop-down-btn"
                onPress={() => {
                  logEvent('UserMgmt', 'search_dropdown');
                  setSearchDropdownVisible(!searchDropdownVisible);
                }}
              >
                <View
                  style={{
                    flex: 1,
                    alignItems: 'flex-end',
                    marginLeft: 12,
                    marginRight: 4,
                  }}
                >
                  <FontAwesome
                    size={20}
                    style={{
                      color: Colors.light.hereworksLightBlue,
                      marginRight: 12,
                    }}
                    name={searchDropdownVisible ? 'angle-up' : 'angle-down'}
                  />
                </View>
                <Text
                  style={[
                    styles.dropdownBoxText,
                    { marginLeft: 0, marginRight: 100 },
                  ]}
                >
                  {searchDropdownSelection}
                </Text>
                <View
                  style={{
                    width: 1,
                    height: 24,
                    backgroundColor: Colors.light.hereworksWhite500,
                  }}
                />
                {searchDropdownVisible && renderSearchDropdown()}
              </Pressable>
              <TextInput
                style={[
                  styles.dropdownBoxText,
                  { outlineWidth: 0, marginLeft: 18 },
                ]}
                placeholder={'Search'}
                testID="um-search-bar-input-field"
                onChangeText={(text) => {
                  setSearchValue(text);
                }}
              />
              <View style={{ alignItems: 'flex-end', marginRight: 15 }}>
                <SearchIcon />
              </View>
            </View>
            {/* <Pressable
              style={[
                styles.dropdownBox,
                {
                  height: 40,
                  justifyContent: 'flex-start',
                  marginRight: 40,
                  width: 227,
                },
              ]}
              onPress={() => setSortDropdownVisible(!sortDropdownVisible)}
            >
              <Text
                style={[
                  styles.dropdownBoxText,
                  { marginLeft: 12, marginRight: 10 },
                ]}
              >
                {sortDropdownSelection}
              </Text>
              <View
                style={{
                  alignItems: 'flex-end',
                  marginRight: 12,
                }}
              >
                <FontAwesome
                  size={20}
                  style={{
                    color: Colors.light.hereworksLightBlue,
                  }}
                  name={sortDropdownVisible ? 'angle-up' : 'angle-down'}
                />
              </View>
              {sortDropdownVisible && renderSortDropdown()}
            </Pressable> */}
          </View>
          <ScrollView>
            <RenderGroupsWeb />
            <RenderUsers />
          </ScrollView>
        </View>
      )}
      {(isDev && selectedSection === 'groups' && isMobile && canAddUserGroups) && (<>
        <LinearGradient
          colors={['rgba(0, 0, 0, 0.0)', 'rgba(0, 0, 0, 0.4)']}
          style={styles.gradientBg}
          pointerEvents='none'
        >
        </LinearGradient>
        <PressableStyled
          style={[styles.addButton, {
            flexDirection: 'row',
            alignItems: 'center',
          }]}
          onPress={() => {
            // setAddGroupsModalVisible(!addGroupsModalVisible);
            userGroupBottomSheetRef.current?.present();
            logEvent('UserMgmt', 'Add_groups');
          }}
          testID='add-user-group-btn'
        >
          <PlusIcon color='#fff' style={{ marginRight: 8 }} />
          <Text style={styles.addButtonText}>{'Add group'}</Text>
        </PressableStyled>
      </>)}
      {(isDev && selectedSection === 'users' && isMobile && canAddUsers) && <>
        <LinearGradient
          colors={['rgba(0, 0, 0, 0.0)', 'rgba(0, 0, 0, 0.4)']}
          style={styles.gradientBg}
          pointerEvents='none'
        >
        </LinearGradient>
        <PressableStyled
          style={[styles.addButton, {
          }]}
          onPress={() => {
            userInviteBottomSheetRef.current?.present();
            logEvent('UserMgmt', 'Invite_users');
          }}
        >
          <PlusIcon color='#fff' style={{ marginRight: 8 }} />
          <Text style={styles.addButtonText}>{'Invite users'}</Text>
        </PressableStyled>
      </>}
      {(isDev && selectedSection === 'roles' && isMobile && canAddRoles && !alertVisible) && <>
        <LinearGradient
          colors={['rgba(0, 0, 0, 0.0)', 'rgba(0, 0, 0, 0.4)']}
          style={styles.gradientBg}
          pointerEvents='none'
        >
        </LinearGradient>
        <PressableStyled
          style={[styles.addButton, {
          }]}
          onPress={() => {
            setEditingRole({ name: '', description: '', permissions: [] });
            logEvent('UserMgmt', 'Add_roles');
          }}
        >
          <PlusIcon color='#fff' style={{ marginRight: 8 }} />
          <Text style={styles.addButtonText}>{'Add roles'}</Text>
        </PressableStyled>
      </>}
      <AlertModal
        modalVisible={alertVisible && !selectedUserGroup}
        error={alertError}
        title={alertTitle}
        message={alertMessage}
        setModalVisible={setAlertVisible}
        action={alertAction}
        actionTitle={alertActionTitle}
        mobileFullScreen={isMobile}
        heading={alertHeading}
      />
      <Modal
        visible={!!selectedRole}
        statusBarTranslucent={false}
        onRequestClose={() => {
          setShowRoleMenu(false);
          setSelectedRole(null);
        }}
      >
        <SafeAreaView
          edges={Platform.OS === 'android' ? [] : ['top']}
          style={{
            flex: 1,
            backgroundColor: Colors.light.hereworksBackground,
          }}>
          <View
            style={{
              flex: 1,
              paddingHorizontal: 24,
              paddingTop: modalTopInset,
            }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 20 }}>
            <Pressable onPress={() => {
              setShowRoleMenu(false);
              setSelectedRole(null);
            }}><BackArrow /></Pressable>
            <View style={{ flex: 1, marginLeft: 8 }}>
              <Text style={{
                fontSize: 22,
                fontWeight: 'bold',
                textAlignVertical: 'center',
              }}>
                {selectedRole?.name}
              </Text>
              <Text style={{
                fontSize: 12,
                color: Colors.light.hereworksBlack300,
                marginTop: 4,
              }}>
                {selectedRole?.description ? selectedRole.description : 'No description'}
              </Text>
            </View>
            <EditIcon24
              onPress={() => {
                setShowRoleMenu(!showRoleMenu);
              }}
            />
          </View>
          <RoleMatrix
            selectedRole={selectedRole} canUpdateRoles={canUpdateRoles} onSave={saveRole} />
          <BottomSheet
            ref={roleBottomSheetRef}
            backdropComponent={showRoleMenu ? (props) => (
              <BottomSheetBackdrop
                {...props}
                disappearsOnIndex={-1}
                appearsOnIndex={0}
              />
            ) : undefined}
            index={-1}
            snapPoints={['30%']}
            backgroundStyle={{ borderRadius: 10 }}
          >
            <BottomSheetView style={{ flex: 1, padding: 20 }}>
              <Text
                style={{
                  fontFamily: 'Poppins_600SemiBold',
                  fontSize: 18,
                  lineHeight: 27,
                  color: Colors.light.hereworksBlack600,
                  marginBottom: 12,
                }}
              >
                {selectedRole?.name}
              </Text>
              <Text
                style={{
                  fontFamily: 'Poppins_400Regular',
                  fontSize: 14,
                  lineHeight: 21,
                  color: Colors.light.hereworksBlack600,
                }}
              >
                {selectedRole?.description}
              </Text>
              <Pressable
                style={(state) => [{
                  marginTop: 24,
                  alignSelf: 'stretch',
                  justifyContent: 'center',
                  backgroundColor: state.pressed
                    ? Colors.light.hereworksBlue100 : 'transparent',
                }]}
                onPress={async () => {
                  setSelectedRole(null);
                  setShowRoleMenu(false);
                  setEditingRole(selectedRole);
                }}
                testID='edit-role-button'
              >
                <Text
                  style={{
                    color: Colors.light.hereworksBlack600,
                    fontFamily: 'Roboto_400Regular',
                    fontSize: 16,
                    lineHeight: 24,
                    marginVertical: 8,
                    marginRight: 8,
                  }}
                  numberOfLines={1}
                >
                  Edit Role
                </Text>
              </Pressable>
              <Pressable
                style={(state) => [{
                  marginTop: 12,
                  alignSelf: 'stretch',
                  justifyContent: 'center',
                  backgroundColor: state.pressed
                    ? Colors.light.hereworksBlue100 : 'transparent',
                }]}
                onPress={() => {
                  roleBottomSheetRef.current?.close();
                  showAlert(
                    'Delete role',
                    <>
                      {'Are you sure you want to delete the role '}
                      <Text style={Style.alertBoldText}>{selectedRole.name}</Text>
                    </>,
                    true,
                    () => deleteRole(selectedRole.id).then((response) => {
                      setSelectedRole(null);
                      if (response?.isError) {
                        showAlert(
                          'Error',
                          response.errorText || SOMETHING_WENT_WRONG,
                          true,
                          () => { },
                        );
                        return;
                      }
                      setUpdateUsers(!updateUsers);
                      showAlert(
                        'Role deleted',
                        'The role has been successfully deleted.',
                        false,
                        () => { },
                      );
                    }).catch((error) => {
                      showAlert(
                        'Error',
                        error.message || SOMETHING_WENT_WRONG,
                        true,
                        () => { },
                      );
                    }),
                    'Delete',
                  );
                  setSelectedRole(null);
                }}
                testID='delete-role-button'
              >
                <Text
                  style={{
                    color: Colors.light.hereworksRed,
                    fontFamily: 'Roboto_400Regular',
                    fontSize: 16,
                    lineHeight: 24,
                    marginVertical: 8,
                    marginRight: 8,
                  }}
                  numberOfLines={1}
                >
                  Delete Role
                </Text>
              </Pressable>
            </BottomSheetView>
          </BottomSheet>
          </View>
        </SafeAreaView>
      </Modal>
      <Modal
        visible={!!selectedUserGroup}
        statusBarTranslucent={false}
        onRequestClose={() => { setSelectedUserGroup(null); }}>
        <SafeAreaView
          edges={Platform.OS === 'android' ? [] : ['top']}
          style={{
            flex: 1,
            backgroundColor: Colors.light.hereworksBackground,
          }}>
          <View
            style={{
              flex: 1,
              padding: 24,
              paddingTop: Platform.OS === 'android' ? modalTopInset : 24,
              backgroundColor: Colors.light.hereworksBackground,
            }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 20 }}>
            <Pressable onPress={() => setSelectedUserGroup(null)}><BackArrow /></Pressable>
            <Text style={{
              fontSize: 22,
              fontFamily: 'Poppins_600SemiBold',
              color: Colors.light.hereworksBlack600,
              marginLeft: 8,
              textAlignVertical: 'center',
              flex: 1,
            }}>
              {selectedUserGroup?.Name}
            </Text>
            <EditIcon24
              onPress={() => {
                setEditUserGroup(!editUserGroup);
              }}
            />
          </View>
          <ScrollView style={{ flex: 1, marginBottom: 20 }}>
            <Tabs
              titleOne="USERS"
              titleTwo="NOTIFICATIONS"
              selected={groupUsersTabSelected}
              setSelected={setGroupUsersTabSelected}
            />
            {(userGroupUsers.length === 0
              && !loading && groupUsersTabSelected) && <ScreenEmptyState
                image={<NoUsersImage />}
                title='No users in this group yet'
                subtitle='Get started by adding users to this group'
                actionAvailable
                buttonTitle='Add Users'
                buttonAction={() => {
                  setEditUserGroup(true);
                }}
              />}
            {groupUsersTabSelected && userGroupUsers.filter(
              (user: any) => user.status === UserStatus.VERIFIED,
            ).map((user: any, key: number) => (
              <View
                key={key}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <UserCardMobile
                  user={user}
                  roles={[]}
                  hideSectionDropdown={() => { setShowSectionDropdown(false); }}
                  groupName={selectedUserGroup?.Name}
                  deleteAction={() => removeUserFromSelectedGroup(user)}
                  userGroups={userGroups}
                  selectedUserGroup={selectedUserGroup}
                />
              </View>
            ))}
            {!groupUsersTabSelected && notificationGroups
              && notificationGroups.length === 0 && !loading && <ScreenEmptyState
                image={<NoUsersImage />}
                title='No notification groups in this group yet'
                subtitle='Get started by adding notification groups to this group'
                actionAvailable={false}
                buttonTitle='Add Notification Groups'
                buttonAction={() => {
                  setEditUserGroup(true);
                }}
              />}
            {!groupUsersTabSelected && notificationGroups && notificationGroups.map(
              (notificationGroup: any, key: number) => (
                <View
                  key={key}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}
                >
                  <NotificationGroupCard
                    title={notificationGroup.Name}
                    issues={notificationGroup.Issues}
                    groupId={selectedUserGroup?.UserGroupId} />
                </View>
              ),
            )}
          </ScrollView>
          <GroupEditModal
            users={allUsers.filter((user: UserInstance) => user.status === UserStatus.VERIFIED)}
            modalVisible={editUserGroup}
            setModalVisible={setEditUserGroup}
            updatedGroup={updatedGroup}
            title={selectedUserGroup?.Name}
            description={selectedUserGroup?.Description}
            groupId={selectedUserGroup?.UserGroupId}
            notificationGroups={[]}
            groupDeleted={() => {
              showAlert(
                DELETE_USER_GROUP_SUCCESS,
                '',
                false,
                () => {
                  setLoading(true);
                  setEditUserGroup(false);
                  setSelectedUserGroup(null);
                },
                '',
                'Delete User Group',
              );
            }}
          />
            <AlertModal
              modalVisible={alertVisible && !!selectedUserGroup}
              error={alertError}
              title={alertTitle}
              message={alertMessage}
              setModalVisible={setAlertVisible}
              action={alertAction}
              actionTitle={alertActionTitle}
              mobileFullScreen={isMobile}
              heading={alertHeading}
            />
          </View>
        </SafeAreaView>
      </Modal>
      <UserGroupBottomSheet
        users={allUsers}
        title={selectedUserGroup?.Name}
        description={selectedUserGroup?.Description}
        groupId={selectedUserGroup?.UserGroupId}
        updatedGroup={() => {
          setUpdateUsers(!updateUsers);
          userGroupBottomSheetRef.current?.dismiss();
        }}
        groupDeleted={() => { }}
        notificationGroups={undefined}
        close={() => {
          userGroupBottomSheetRef.current?.dismiss();
        }}
        userGroupSheetRef={userGroupBottomSheetRef}
      />
      <EditRoleModal
        isVisible={editingRole !== null}
        onClose={() => {
          setEditingRole(null);
        }}
        roleName={editingRole?.name}
        role={editingRole}
        onSave={saveRole}
      />
    </View>
  ) : (
    <ScreenEmptyState
      title={noPermissionsTitle('user groups or users')}
      subtitle={noPermissionsSubtitle('user groups or users')}
      large
      image={<NoUsersImage />}
    />
  );
}

const styles = StyleSheet.create({
  scrollContainer: {
    backgroundColor: Colors.light.hereworksBackground,
    flexGrow: 1,
    paddingBottom: 100,
  },
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-start',
    backgroundColor: Colors.light.hereworksBackground,
    paddingTop: 130,
    marginTop: -90,
  },
  dropdownBox: {
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 10,
    marginTop: 4,
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    zIndex: 2,
  },
  dropdownBoxText: {
    fontFamily: 'Poppins_400Regular',
    fontSize: isMobile ? 12 : 14,
    color: Colors.light.hereworksBlack600,
    paddingVertical: 8,
    marginLeft: 12,
    flex: 1,
  },
  dropdownItemText: {
    marginLeft: 12,
    marginVertical: 9,
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    color: Colors.light.hereworksPrimaryGrey,
  },
  dropdownView: {
    position: 'absolute',
    top: 61,
    left: 0,
    right: 0,
    zIndex: 5,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksPaleBlue900,
    backgroundColor: '#fff',
    overflow: 'hidden',
  },
  buttonCommon: {
    borderRadius: 4,
    borderWidth: 1,
    alignSelf: 'stretch',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerText: {
    fontFamily: 'Roboto_500Medium',
    fontSize: 10,
    lineHeight: 15,
    letterSpacing: 0.7,
    color: Colors.light.hereworksBlack400,
  },
  addButton: {
    position: 'absolute',
    bottom: 0,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    backgroundColor: Colors.light.hereworksBlue500,
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    zIndex: 10,
    marginBottom: 30,
  },
  addButtonText: {
    color: '#fff',
    fontFamily: 'Roboto_500Medium',
    fontSize: 16,
    lineHeight: 24,
  },
  gradientBg: {
    position: 'absolute',
    bottom: 0,
    zIndex: 10,
    left: 0,
    right: 0,
    alignItems: 'center',
    padding: 60,
  },
  indicationDot: {
    position: 'absolute',
    top: 5,
    end: 5,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: Colors.light.hereworksRed500,
  },
});
